import React, { useState } from 'react';
import { useSite } from '../../context/SiteContext';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import { getSiteDisplayAddress, getSitePublicUrl } from '../../utils/security';
import {
  MapPin,
  Globe,
  Search,
  CheckCircle2,
  Copy,
  Check,
  Building,
  Phone,
  Clock,
  Sparkles,
  Save,
  Code,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';

export const LocalSeoManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite, updateSiteData } = useSite();
  const [copiedSchema, setCopiedSchema] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Yerel SEO & Schema İçin Aktif Web Sitesi Gereklidir"
        description="Google Haritalar ve yerel aramalarda üst sıralara çıkmak için işletme schema ve konum ayarlarını yönetmek üzere lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const s = activeSite?.settings || {};
  const businessName = s.businessName || activeSite.name;
  const phone = s.phone || '+90 (252) 692 00 00';
  const address = s.address || 'Dalaman / Muğla';
  const city = s.city || 'Muğla';
  const district = s.district || 'Dalaman';
  const serviceAreas = s.serviceAreas || ['Dalaman', 'Ortaca', 'Köyceğiz', 'Göcek', 'Fethiye'];
  const domain = getSiteDisplayAddress(activeSite);
  const canonicalUrl = getSitePublicUrl(activeSite);

  // Generate dynamic Schema.org JSON-LD LocalBusiness markup
  const jsonLdSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: businessName,
    image: s.logoUrl || 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=800',
    '@id': canonicalUrl,
    url: canonicalUrl,
    telephone: phone,
    priceRange: '₺₺',
    address: {
      '@type': 'PostalAddress',
      streetAddress: address,
      addressLocality: district,
      addressRegion: city,
      postalCode: '48770',
      addressCountry: 'TR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 36.7667,
      longitude: 28.8033,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '08:30',
        closes: '19:30',
      },
    ],
    areaServed: serviceAreas.map((area) => ({
      '@type': 'AdministrativeArea',
      name: area,
    })),
  };

  const schemaString = JSON.stringify(jsonLdSchema, null, 2);

  const handleCopySchema = () => {
    navigator.clipboard.writeText(schemaString);
    setCopiedSchema(true);
    setTimeout(() => setCopiedSchema(false), 2000);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2 text-emerald-600">
            <MapPin className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">Yerel Arama Motoru Optimizasyonu</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Yerel SEO (Local Business Schema)
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 max-w-2xl">
            Google Haritalar, "Yakınımdaki Hizmetler" ve yerel aramalarda ilk sıralara çıkmak için işletmenizin yapılandırılmış veri (JSON-LD) ve hizmet bölgesi ayarları.
          </p>
        </div>
      </div>

      {/* Local SEO Status & Health */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase">NAP Bütünlüğü</span>
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="text-lg font-black text-slate-900">Uyumlu (%100)</div>
          <p className="text-[11px] text-slate-400">İsim, Adres ve Telefon tüm sitede merkezi veritabanından çekiliyor.</p>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase">Hizmet Bölgeleri</span>
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-bold">
              {serviceAreas.length} Bölge
            </span>
          </div>
          <div className="text-lg font-black text-slate-900">{city} / {district}</div>
          <p className="text-[11px] text-slate-400">{serviceAreas.join(', ')}</p>
        </div>

        <div className="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-500 uppercase">Schema.org Türü</span>
            <Code className="w-4 h-4 text-indigo-600" />
          </div>
          <div className="text-lg font-black text-slate-900">LocalBusiness</div>
          <p className="text-[11px] text-slate-400">Google Arama Motoru botlarına otomatik sunulur.</p>
        </div>
      </div>

      {/* JSON-LD Schema Box */}
      <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Code className="w-5 h-5 text-indigo-600" />
            <h2 className="text-base font-bold text-slate-900">Google İçin Üretilen Yapılandırılmış Veri (JSON-LD)</h2>
          </div>
          <button
            type="button"
            onClick={handleCopySchema}
            className="px-3.5 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition flex items-center gap-1.5"
          >
            {copiedSchema ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copiedSchema ? 'Kopyalandı' : 'Kodu Kopyala'}</span>
          </button>
        </div>

        <p className="text-xs text-slate-500 leading-relaxed">
          Bu kod sitenizin kaynak kodundaki <code className="bg-slate-100 px-1 py-0.5 rounded text-indigo-600 font-mono">&lt;head&gt;</code> etiketine otomatik olarak enjekte edilir. Google botları bu verileri okuyarak işletmenizin telefonunu, çalışma saatlerini ve bölgesini arama sonuçlarında zengin kart olarak gösterir.
        </p>

        <pre className="p-4 rounded-xl bg-slate-950 text-slate-200 text-xs font-mono overflow-x-auto border border-slate-800 leading-relaxed max-h-96">
          {schemaString}
        </pre>
      </div>
    </div>
  );
};
